import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { DeskPhoneSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/**
 * A modelled office desk phone (base + sloped keypad + cradled handset) sitting
 * on the reception counter. When `ringing` is true it PULSES: the status LED +
 * a glow halo brighten and dim on a sine wave, so the ringing phone is visually
 * obvious behind the incoming-call overlay. A real 3D prop (not a DOM card),
 * mounted regardless of `scenario.walkable` — mirrors ReceptionTerminal.
 */
export function DeskPhone({ spec, ringing }: { spec: DeskPhoneSpec; ringing: boolean }) {
  const scale = spec.scale ?? 1;
  const yaw = (spec.rotationDeg ?? 0) * DEG;

  const ledRef = useRef<THREE.MeshStandardMaterial>(null);
  const haloRef = useRef<THREE.Mesh>(null);
  const haloMat = useRef<THREE.MeshStandardMaterial>(null);
  const handset = useRef<THREE.Group>(null);
  const t = useRef(0);

  useFrame((_, dt) => {
    t.current += dt;
    // Fast blink while ringing; a dim steady LED otherwise.
    const pulse = ringing ? 0.5 + 0.5 * Math.sin(t.current * 9) : 0;
    if (ledRef.current) ledRef.current.emissiveIntensity = ringing ? 0.6 + pulse * 2.6 : 0.25;
    if (haloMat.current) haloMat.current.opacity = ringing ? 0.12 + pulse * 0.4 : 0;
    if (haloRef.current) {
      const s = ringing ? 1 + pulse * 0.35 : 1;
      haloRef.current.scale.setScalar(s);
      haloRef.current.visible = ringing;
    }
    // Subtle handset jiggle in the cradle while ringing.
    if (handset.current) {
      handset.current.rotation.z = ringing ? Math.sin(t.current * 22) * 0.02 : 0;
    }
  });

  const bodyColor = "#1c2128";
  const trimColor = "#2b323b";

  return (
    <group position={spec.position} rotation={[0, yaw, 0]} scale={scale}>
      {/* Base wedge (sloped up toward the back). */}
      <mesh position={[0, 0.022, 0]} rotation={[-8 * DEG, 0, 0]}>
        <boxGeometry args={[0.2, 0.04, 0.16]} />
        <meshStandardMaterial color={bodyColor} metalness={0.3} roughness={0.55} />
      </mesh>
      {/* Keypad plate. */}
      <mesh position={[0, 0.043, 0.01]} rotation={[-8 * DEG, 0, 0]}>
        <boxGeometry args={[0.13, 0.006, 0.11]} />
        <meshStandardMaterial color={trimColor} metalness={0.2} roughness={0.7} />
      </mesh>
      {/* Keypad dots (3x4). */}
      {Array.from({ length: 12 }).map((_, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        return (
          <mesh
            key={i}
            position={[(col - 1) * 0.032, 0.048, -0.028 + row * 0.026]}
            rotation={[-8 * DEG, 0, 0]}
          >
            <cylinderGeometry args={[0.008, 0.008, 0.004, 12]} />
            <meshStandardMaterial color="#3a424c" />
          </mesh>
        );
      })}
      {/* Cradle posts at the back. */}
      <mesh position={[-0.085, 0.06, -0.05]}>
        <boxGeometry args={[0.02, 0.05, 0.05]} />
        <meshStandardMaterial color={bodyColor} roughness={0.6} />
      </mesh>
      <mesh position={[0.085, 0.06, -0.05]}>
        <boxGeometry args={[0.02, 0.05, 0.05]} />
        <meshStandardMaterial color={bodyColor} roughness={0.6} />
      </mesh>
      {/* Handset resting across the cradle. */}
      <group ref={handset} position={[0, 0.086, -0.05]}>
        <mesh>
          <boxGeometry args={[0.215, 0.03, 0.045]} />
          <meshStandardMaterial color="#23292f" roughness={0.5} />
        </mesh>
        <mesh position={[-0.088, -0.018, 0]}>
          <boxGeometry args={[0.04, 0.02, 0.05]} />
          <meshStandardMaterial color="#23292f" roughness={0.5} />
        </mesh>
        <mesh position={[0.088, -0.018, 0]}>
          <boxGeometry args={[0.04, 0.02, 0.05]} />
          <meshStandardMaterial color="#23292f" roughness={0.5} />
        </mesh>
      </group>
      {/* Status LED (pulses red while ringing). */}
      <mesh position={[0.07, 0.05, 0.05]} rotation={[-8 * DEG, 0, 0]}>
        <cylinderGeometry args={[0.006, 0.006, 0.004, 12]} />
        <meshStandardMaterial
          ref={ledRef}
          color="#ff5a4d"
          emissive="#ff5a4d"
          emissiveIntensity={0.25}
          toneMapped={false}
        />
      </mesh>
      {/* Glow halo (a soft flat ring on the counter that pulses while ringing). */}
      <mesh ref={haloRef} position={[0, 0.004, 0]} rotation={[-Math.PI / 2, 0, 0]} visible={false}>
        <ringGeometry args={[0.14, 0.24, 40]} />
        <meshStandardMaterial
          ref={haloMat}
          color="#ffb14a"
          emissive="#ffb14a"
          emissiveIntensity={1.2}
          transparent
          opacity={0}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}
